import { useEffect, useRef, useState, Children, cloneElement, isValidElement } from "react";

/**
 * Reveal — wraps content and adds a fade-up animation that triggers
 * once the element scrolls into view, instead of firing on page load.
 * Usage: <Reveal>...</Reveal> or <Reveal stagger>...</Reveal> for children.
 */
export default function Reveal({ children, stagger = false, className = "", as: Tag = "div", delay = 0 }) {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.15, rootMargin: "0px 0px -60px 0px" }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    if (stagger) {
        const kids = Children.toArray(children);
        return (
            <Tag ref={ref} className={className}>
                {kids.map((child, i) => (
                    <div
                        key={i}
                        style={{
                            opacity: visible ? 1 : 0,
                            transform: visible ? "translateY(0)" : "translateY(14px)",
                            transition: `opacity 0.8s cubic-bezier(0.2,0.7,0.2,1) ${delay + i * 0.08}s, transform 0.8s cubic-bezier(0.2,0.7,0.2,1) ${delay + i * 0.08}s`,
                        }}
                    >
                        {child}
                    </div>
                ))}
            </Tag>
        );
    }

    return (
        <Tag
            ref={ref}
            className={className}
            style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(16px)",
                transition: `opacity 0.9s cubic-bezier(0.2,0.7,0.2,1) ${delay}s, transform 0.9s cubic-bezier(0.2,0.7,0.2,1) ${delay}s`,
            }}
        >
            {children}
        </Tag>
    );
}
